import { Concertina } from "@components/Concertina/Concertina";
import { JWMHtml } from "@components/JWMHtml/JWMHtml";
import { Typography } from "@jmc/solid-design-system/src/components/atoms/Typography/Typography";
import { CMSSectionProps } from "@jwm/types/CMSSectionProps";
import { default as cmsMapper } from "@utils/cms-mapper";
import React from "react";

import style from "../../style.module.scss";
import { trackingHelpersProps } from "../types";

interface FaqAccordionProps {
    accordion_type?: string;
    panes?: {
        pane?: {
            answer?: string;
            question?: string;
        };
    }[];
}

interface FaqTextProps {
    answer?: string;
    question?: string;
}

interface FaqContentProps {
    accordion?: FaqAccordionProps;
    text?: FaqTextProps;
}

interface FaqBlockProps {
    content: FaqContentProps;
    placement?: string;
    locale?: string;
    trackingHelpers: trackingHelpersProps;
    sectionIndex?: number;
    siteSpecialty?: {
        type: string;
        title: string;
    };
    sectionContent?: CMSSectionProps;
    title?: string;
    jnjFullBranded?: boolean;
}

const buildFaqAccordion = (
    accordion: FaqAccordionProps,
    placement: string,
    siteSpecialty: { type: string; title: string },
    jnjFullBranded: boolean,
): JSX.Element => {
    const type = cmsMapper.mapConcertinaType(accordion?.accordion_type);
    const items =
        accordion?.panes &&
        accordion?.panes?.map(({ pane }) => {
            const title = <JWMHtml showGlossary={false}>{pane?.question}</JWMHtml>;
            const titleString = pane?.question;
            const content = <JWMHtml siteSpecialty={siteSpecialty}>{pane?.answer}</JWMHtml>;
            return {
                title,
                titleString,
                content,
            };
        });
    return (
        <div className={style.concertina}>
            {items?.length && (
                <Concertina
                    items={items}
                    type={type}
                    placement={placement}
                    siteSpecialty={siteSpecialty}
                    titleComponent={jnjFullBranded ? "h5" : "h3"}
                />
            )}
        </div>
    );
};

const buildFaqText = (
    text: FaqTextProps,
    sectionIndex: number,
    siteSpecialty: { type: string; title: string },
    jnjFullBranded: boolean,
): JSX.Element => {
    const faqBlock = (
        <>
            <Typography variant={jnjFullBranded ? "h5" : "h3"}>
                <JWMHtml showGlossary={false} loading={sectionIndex === 0 ? "eager" : "lazy"}>
                    {text?.question}
                </JWMHtml>
            </Typography>
            {jnjFullBranded ? (
                <Typography variant="body-02">
                    <JWMHtml siteSpecialty={siteSpecialty} loading={sectionIndex === 0 ? "eager" : "lazy"}>
                        {text?.answer}
                    </JWMHtml>
                </Typography>
            ) : (
                <JWMHtml siteSpecialty={siteSpecialty} loading={sectionIndex === 0 ? "eager" : "lazy"}>
                    {text?.answer}
                </JWMHtml>
            )}
        </>
    );

    return jnjFullBranded ? <div className={style.faqBlock}>{faqBlock}</div> : faqBlock;
};

export const FaqBlock = ({
    content,
    placement,
    sectionIndex,
    siteSpecialty,
    title,
    jnjFullBranded,
}: FaqBlockProps): JSX.Element => {
    return (
        <>
            {title && (
                <Typography variant="h3">
                    <JWMHtml>{title}</JWMHtml>
                </Typography>
            )}
            {content?.length &&
                content?.map((content) => {
                    if (content?.accordion)
                        return buildFaqAccordion(content?.accordion, placement, siteSpecialty, jnjFullBranded);
                    if (content?.text) return buildFaqText(content?.text, sectionIndex, siteSpecialty, jnjFullBranded);
                })}
        </>
    );
};
